import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { getCartEntries } from '../../util/cart';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const { UUID } = sdkTypes;

const getCartListingIds = state => {
  const cart = state.cart?.cart || {};
  return getCartEntries(cart)
    .map(entry => entry.listingId)
    .filter(Boolean)
    .map(id => new UUID(id));
};

const fetchCartListingsPayloadCreator = ({ listingIds, config }, thunkAPI) => {
  const { dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  if (!listingIds.length) {
    return Promise.resolve([]);
  }

  return sdk.listings
    .query(
      {
        ids: listingIds,
        include: ['author', 'author.profileImage', 'images', 'currentStock'],
        'fields.image': [
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          'variants.square-small',
          'variants.square-small2x',
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      },
      { expand: true }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response.data.data.map(entity => ({ id: entity.id, type: entity.type }));
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCartListingsThunk = createAsyncThunk(
  'CartPage/fetchCartListings',
  fetchCartListingsPayloadCreator
);

export const loadCartListings = config => (dispatch, getState) => {
  dispatch(startCartPageLoading());

  return Promise.resolve()
    .then(() => {
      const listingIds = getCartListingIds(getState());
      return dispatch(fetchCartListingsThunk({ listingIds, config }));
    })
    .catch(e => {
      dispatch(finishCartPageLoading(storableError(e)));
      throw e;
    });
};

const initialState = {
  listingRefs: [],
  fetchCartListingsInProgress: false,
  fetchCartListingsError: null,
  cartPageLoaded: false,
};

const cartPageSlice = createSlice({
  name: 'CartPage',
  initialState,
  reducers: {
    startCartPageLoading: state => {
      state.listingRefs = [];
      state.fetchCartListingsInProgress = true;
      state.fetchCartListingsError = null;
      state.cartPageLoaded = false;
    },
    finishCartPageLoading: (state, action) => {
      state.fetchCartListingsInProgress = false;
      state.fetchCartListingsError = action.payload || null;
      state.cartPageLoaded = true;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCartListingsThunk.pending, state => {
        state.fetchCartListingsInProgress = true;
        state.fetchCartListingsError = null;
      })
      .addCase(fetchCartListingsThunk.fulfilled, (state, action) => {
        state.fetchCartListingsInProgress = false;
        state.listingRefs = action.payload;
        state.cartPageLoaded = true;
      })
      .addCase(fetchCartListingsThunk.rejected, (state, action) => {
        state.fetchCartListingsInProgress = false;
        state.fetchCartListingsError = action.payload;
        state.cartPageLoaded = true;
      });
  },
});

export const { startCartPageLoading, finishCartPageLoading } = cartPageSlice.actions;
export default cartPageSlice.reducer;

export const loadData = (params, search, config) => dispatch => {
  return dispatch(loadCartListings(config)).catch(() => {
    return Promise.resolve(null);
  });
};
